import { number, optional, string, zodLess, ZodLessInfer, zrecord } from '@kaiizen/zod-less';

export const JevQuestionSchema = zodLess({
  type: string,
  instructions: string,
  criteria: optional(zrecord(string)),
});

export type JevQuestion = ZodLessInfer<typeof JevQuestionSchema>;

/**
 * Caps on the JEV proxy payload, set at about twice the largest real voice-scope projection
 * (base template, every room fully scoped, full transcript window: 10,476 chars of state,
 * 4 questions, 83 criteria, 31,394 chars total). Criteria per question stays at the projection's
 * own 254-choice limit (plus `none`) so larger org templates are not cut off.
 */
export const JEV_MAXIMUM_STATE_LENGTH = 24_000;
export const JEV_MAXIMUM_QUESTIONS = 8;
export const JEV_MAXIMUM_CRITERIA_PER_QUESTION = 256;
export const JEV_MAXIMUM_TOTAL_TEXT_LENGTH = 64_000;

const jevQuestionTextLength = ({ instructions, criteria = {} }: JevQuestion) =>
  Object.values(criteria).reduce((total, criterion) => total + criterion.length, instructions.length);

export const JevEvaluatePayloadSchema = zodLess({
  scanId: string,
  state: string.max(JEV_MAXIMUM_STATE_LENGTH),
  questions: zrecord(JevQuestionSchema)
    .refine(questions => Object.keys(questions).length <= JEV_MAXIMUM_QUESTIONS, {
      message: `At most ${JEV_MAXIMUM_QUESTIONS} questions`,
    })
    .refine(questions => Object.values(questions).every(({ criteria = {} }) => Object.keys(criteria).length <= JEV_MAXIMUM_CRITERIA_PER_QUESTION), {
      message: `At most ${JEV_MAXIMUM_CRITERIA_PER_QUESTION} criteria per question`,
    }),
}).refine(
  ({ state, questions }) =>
    Object.values(questions).reduce((total, question) => total + jevQuestionTextLength(question), state.length) <= JEV_MAXIMUM_TOTAL_TEXT_LENGTH,
  {
    message: `JEV payload text exceeds ${JEV_MAXIMUM_TOTAL_TEXT_LENGTH} characters`,
  },
);

export type JevEvaluatePayload = ZodLessInfer<typeof JevEvaluatePayloadSchema>;

export const JevAnswerSchema = zodLess({
  type: string,
  noul: optional(number),
  choice: optional(string),
  confidence: optional(number),
  probabilities: optional(zrecord(number)),
});

export type JevAnswer = ZodLessInfer<typeof JevAnswerSchema>;

export const JevUsageSchema = zodLess({
  input_tokens: optional(number),
  output_tokens: optional(number),
});

export const JevEvaluateResponseSchema = zodLess({
  model: string,
  answers: zrecord(JevAnswerSchema),
  usage: optional(JevUsageSchema),
});

export type JevEvaluateResponse = ZodLessInfer<typeof JevEvaluateResponseSchema>;
